import React, { useEffect, useRef, useState } from 'react';
import Button from '../../components/Button/Button';
import { Link, useNavigate } from 'react-router-dom';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { bankInfo, currency, invoiceItem } from '../../utils/constant';
import { useTranslation } from 'react-i18next';
import { Trans } from 'react-i18next';
import useFormValidation from '../../hooks/useFormValidation';
import { accomodationFormValidationSchema, validate } from '../../utils/formValidation';
import { convertToSubcurrency, normalizeDate } from '../../utils/utility';
import { getPassportById } from '../../services/studentApi';
import { errorToast } from '../../utils/errorHandling';
import { AiOutlineLoading } from 'react-icons/ai';
import { toast } from 'react-toastify';
import { Elements } from "@stripe/react-stripe-js";
import { postAccomodationById, getAccommodationRoomQuantity } from '../../services/accomodationApi';
import { loadStripe } from '@stripe/stripe-js';
import Checkout from '../../components/Checkout';
import AccommodationOption from '../../components/AccommodationOption';

dayjs.extend(utc);

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);
const base_url = process.env.REACT_APP_BASE_URL;

const CreateAccomodation = () => {
    const [courseData, setCourseData] = useState(
        localStorage.getItem("courseData")
            ? JSON.parse(localStorage.getItem("courseData"))
            : null
    );
    const languageSelected = localStorage.getItem('i18nextLng');
    const { t } = useTranslation();
    const currencyCopy = currency(t);
    const bankInfoCopy = bankInfo(t);
    const invoiceItemCopy = invoiceItem(t)
    const kingRoom1PPrice = invoiceItem(t).find((item) => item.key === "king_1p").amount;
    const kingRoom2PPrice = invoiceItem(t).find((item) => item.key === "king_2p").amount;
    const twinRoom2PPrice = invoiceItem(t).find((item) => item.key === "twin_2p").amount;
    const extendStayPrice = invoiceItem(t).find((item) => item.key === "extendStay").amount;
    const [formSection, setFormSection] = useState(1);
    const [formData, setFormData] = useState(null);
    const [roomQuantity, setRoomQuantity] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [passportRegion, setPassportRegion] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isPledgeChecked, setIsPledgeChecked] = useState(false);
    const { errors, validateField, validateAllFields } = useFormValidation(accomodationFormValidationSchema(t, courseData));
    const navigate = useNavigate();
    const [extendStayDay, setExtendStayDay] = useState(0);
    const [earlyStayDay, setEarlyStayDay] = useState(0);
    const [formPercentage, setFormPercentage] = useState("33%");
    const [selectedRoom, setSelectedRoom] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState('');
    const [currencySelected, setCurrencySelected] = useState({
        key: "",
        currency: "",
        rate: 1,
    });
    const [checkoutItem, setCheckoutItem] = useState({
        selectedRoomAmount: 0,
        earlyStayAmount: 0,
        extendStayAmount: 0,
        tourismTax: 0,
        sustainabilityFee: 0,
    });
    // uncomment because removal of transaction fee
    // const [transactionFee, setTransactionFee] = useState(0);
    const [totalAmount, setTotalAmount] = useState({ totalAmount: 0, totalAmountInCurrencySelected: 0 });
    const [netTotalAmount, setNetTotalAmount] = useState(0);

    const fetchPassport = async () => {
        try {
            const data = await getPassportById();
            if (data) {
                setPassportRegion(data.passport_region);
            }
        } catch (error) {
            errorToast(error, t("Passport.warning"), t);
        }
    };
    const fetchRoomQuantity = async () => {
        try {
            const data = await getAccommodationRoomQuantity(courseData.id);
            setRoomQuantity(data);
            return data;
        } catch (error) {
            throw error;
        }
    };

    useEffect(() => {
        setFormData({
            room_type: "",
            check_in_date: "",
            is_early_check_in: "",
            check_out_date: "",
            is_extend_stay: "",
            is_addon_bed: "",
            is_roommate: "",
            roommate_id: "",
            roommate_name: "",
            roommate_relationship: "",
            payment_mode: "",
            offline_payment_currency: "",
            offline_payment_prove: "",
        })

        const init = async () => {
            try {
                await fetchPassport();
                await fetchRoomQuantity();
            } catch (error) {
                errorToast(error, t("general.ActionFailed"), t);
            } finally {
                setIsLoading(false);
            }
        };
        init();

    }, [])

    useEffect(() => {
        // calculate early stay and extend stay amount
        setCheckoutItem((prevState) => ({
            ...prevState,
            earlyStayAmount: earlyStayDay * invoiceItemCopy.filter((item) => item.key === "earlyStay").map((item) => item.amount)[0],
            extendStayAmount: extendStayDay * invoiceItemCopy.filter((item) => item.key === "extendStay").map((item) => item.amount)[0],
        }));
        // calculate tourist tax if not malaysia passport
        if (passportRegion && passportRegion !== "Malaysia") {
            setCheckoutItem((prevState) => ({
                ...prevState,
                tourismTax: (extendStayDay + earlyStayDay + 6) * invoiceItemCopy.filter((item) => item.key === "tourismTax").map((item) => item.amount)[0],
            }));
        }

        // set is_early_check_in and is_extend_stay in form data
        setFormData((prevState) => ({
            ...prevState,
            is_early_check_in: earlyStayDay !== 0 ? 1 : 0,
            is_extend_stay: extendStayDay !== 0 ? 1 : 0,
        }))

        // calculate sustainability fee
        setCheckoutItem((prevState) => ({
            ...prevState,
            sustainabilityFee: (extendStayDay + earlyStayDay + 6) * invoiceItemCopy.filter((item) => item.key === "sustainabilityFee").map((item) => item.amount)[0],
        }));

    }, [earlyStayDay, extendStayDay, passportRegion]);

    useEffect(() => {
        let totalNet = (Number(checkoutItem.earlyStayAmount) +
            Number(checkoutItem.extendStayAmount) +
            Number(checkoutItem.selectedRoomAmount)
        ).toFixed(2);
        let totalSum = (Number(totalNet) + Number(checkoutItem.tourismTax) + Number(checkoutItem.sustainabilityFee)).toFixed(2);
        let totalInCurrencySelected = (Number(totalSum) * Number(currencySelected.rate)).toFixed(2);
        setNetTotalAmount(totalNet);
        setTotalAmount({
            totalAmount: totalSum,
            totalAmountInCurrencySelected: totalInCurrencySelected
        });

    }, [checkoutItem, currencySelected]);

    // uncomment because removal of transaction fee
    // useEffect(() => {
    //     setTransactionFee(paymentMethod === "creditcard" ? ((0.04 * (Number(netTotalAmount) + Number(checkoutItem.tourismTax)) + 1) / 0.96).toFixed(2) : 0);
    // }, [totalAmount, paymentMethod])

    const navigateFormSection = (forward) => {
        let validationFields = [];
        if (forward && formSection === 3) {
            validationFields = ["room_type", "is_roommate", "check_in_date", "check_out_date", "payment_mode"]
            formData.is_roommate === "1" && validationFields.push("roommate_id", "roommate_name", "roommate_relationship")
            formData.payment_mode === "offline" && validationFields.push("offline_payment_currency", "offline_payment_prove")
        };

        if (validateAllFields(formData, validationFields) && (isPledgeChecked || formSection === 1)) {
            const section = forward ? formSection + 1 : formSection - 1;
            setFormSection(section);
            const percentage = (section / 3) * 100;
            const percentageString = percentage + "%";
            setFormPercentage(percentageString);
        } else {
            toast.error(t("general.ValidateFailed"));
        }
    }

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [formSection])

    const isRoomDisabled = (type) => {
        // the type should follow setting from db, only have double room and single room
        // 1. Check if data exists
        if (!roomQuantity || roomQuantity.length === 0) return false;

        // 2. Find the specific room type in the array
        const room = roomQuantity.find(r => r.room_type === type);

        // 3. Disable if room is found and remaining_quantity is 0 or less
        // If room type isn't found in the DB, you might want to default to disabled (true)
        return room ? room.remaining_quantity <= 0 : true;
    };

    const handleCurrencyChange = (e) => {
        const { id, value } = e.target;
        const currencyInfo = currencyCopy.filter((item) => item.key === value);
        setCurrencySelected({
            key: currencyInfo.length >= 1 ? currencyInfo[0].key : "",
            currency: currencyInfo.length >= 1 ? currencyInfo[0].currency : "",
            rate: currencyInfo.length >= 1 ? currencyInfo[0].rate : 1
        });
        setFormData((prevState) => ({
            ...prevState,
            [id]: value,
        }));
        validateField(id, value)
    }

    const handleInputChange = (e) => {
        const { id, value, name } = e.target;

        if (name === "is_addon_bed" || name === "payment_mode") {
            setFormData((prevState) => ({
                ...prevState,
                [name]: id,
            }));
            if (name === "payment_mode") {
                setPaymentMethod(id);
                setCurrencySelected({
                    key: "",
                    currency: "",
                    rate: 1
                });
            }
            validateField(name, id);

        } else if (name === "is_roommate") {
            if (id === "1") {
                setFormData((prevState) => ({
                    ...prevState,
                    [name]: id,
                }));
            } else {
                setFormData((prevState) => ({
                    ...prevState,
                    [name]: id,
                    roommate_id: "",
                    roommate_name: "",
                    roommate_relationship: ""
                }));
            }
            validateField(name, id);
        } else if (id === "check_in_date" || id === "check_out_date") {
            setFormData((prevState) => ({
                ...prevState,
                [id]: value,
            }));
            validateField(id, value);
            if (id === "check_in_date") {
                const desiredStartDate = normalizeDate(value);
                const startDate = normalizeDate(
                    dayjs(courseData.start_datetime)
                        .utc()
                        .format("YYYY-MM-DD")
                );
                setEarlyStayDay(
                    (startDate - desiredStartDate) / (1000 * 60 * 60 * 24) // Convert milliseconds to days
                );
            } else {
                const desiredEndDate = normalizeDate(value);
                const endDate = normalizeDate(
                    dayjs(courseData.end_datetime)
                        .utc()
                        .format("YYYY-MM-DD")
                );
                setExtendStayDay(
                    (desiredEndDate - endDate) / (1000 * 60 * 60 * 24) // Convert milliseconds to days
                );
            }

        } else {
            setFormData((prevState) => ({
                ...prevState,
                [id]: value,
            }));
            validateField(id, value);
        }
    };

    const handleCancel = () => {
        navigate(`${base_url}/view-accommodation`);
    };

    const [previews, setPreviews] = useState({});
    const [fileError, setFileError] = useState(null);
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    const handleFileChange = (event) => {
        const { id } = event.target;
        const selectedFile = event.target.files[0];
        const allowedTypes = [
            "image/jpeg",
            "image/png",
            "application/pdf",
            "image/jpg",
            "image/heic"
        ];
        if (selectedFile && !allowedTypes.includes(selectedFile.type)) {
            setFileError(t("FormValid.FileExtensionAllow"));
            event.target.value = ""; // Clear the file input
            setPreviews((prev) => ({ ...prev, [id]: null }));
            return;
        } else if (selectedFile && selectedFile.size > maxSize) {
            setFileError((prevState) => ({
                ...prevState,
                [id]: t("FormValid.smallerFile10MB"),
            }));
            event.target.value = ""; // Clear the file input
            setPreviews((prev) => ({ ...prev, [id]: null }));
            return;
        }
        setFileError((prevState) => ({
            ...prevState,
            [id]: null,
        }));

        setFormData((prevState) => ({
            ...prevState,
            [id]: selectedFile,
        }));

        if (selectedFile && selectedFile.type.startsWith("image/")) {
            const previewUrl = URL.createObjectURL(selectedFile);
            setPreviews((prev) => ({
                ...prev,
                [id]: previewUrl,
            }));
        } else {
            // If PDF or non-image → no preview
            setPreviews((prev) => ({
                ...prev,
                [id]: null,
            }));
        }

        validateField(id, selectedFile);
    };

    const handleRoomSelection = (roomId) => {
        setSelectedRoom(roomId);
        setFormData((prevState) => ({
            ...prevState,
            "room_type": roomId,
            "is_roommate": roomId === "king_1p" ? "0" : prevState.is_roommate,
        }));
        validateField("room_type", roomId);

        setCheckoutItem((prevState) => ({
            ...prevState,
            selectedRoomAmount: invoiceItemCopy.filter((item) => item.key === roomId).map((item) => item.amount)[0],
        }));
    };
    const setNullFormToEmpty = (nullFormData) => {
        for (const [key, value] of Object.entries(nullFormData)) {
            if (value === null || value === undefined) {
                nullFormData[key] = ''; // Replace with an empty string
            }
        }
        return nullFormData;
    }
    const generateValidationFields = () => {
        let validationFields = ["room_type", "is_roommate", "check_in_date", "check_out_date", "payment_mode"];
        formData.is_roommate === "1" && validationFields.push("roommate_id", "roommate_name", "roommate_relationship");
        formData.payment_mode === "offline" && validationFields.push("offline_payment_currency", "offline_payment_prove");
        return validationFields;
    }

    const isFull = async () => {
        const freshData = await fetchRoomQuantity(courseData.id);
        let dbSearchType = formData.room_type;
        if (formData.room_type === "twin_2p" || formData.room_type === "king_2p") {
            dbSearchType = "doubleroom";
        } else if (formData.room_type === "king_1p") {
            dbSearchType = "singleroom";
        }
        const matchedRoom = freshData.find(r => r.room_type === dbSearchType);
        return matchedRoom ? matchedRoom.remaining_quantity <= 0 : true;
    }
    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            let transformedFormData = setNullFormToEmpty(formData);
            let validationFields = generateValidationFields();

            if (validateAllFields(transformedFormData, validationFields)) {
                // check again room availability before submitting
                const isRoomFull = await isFull();

                if (isRoomFull) {
                    setIsSubmitting(false);
                    toast.error(t("accomodation.RoomUnavailable"));
                    return;
                } else {
                    const combinedFormData = {
                        ...formData,
                        ...checkoutItem,
                        ...totalAmount,
                        // transactionFee: transactionFee,
                        netTotalAmount: netTotalAmount,
                        payment_type: "Payment"
                    };
                    const response = await postAccomodationById(combinedFormData);
                    if (response) {
                        toast.success(t("accomodation.SubmitSuccess"));
                        navigate(`${base_url}/view-accommodation`);
                    }
                }

            } else {
                toast.error(t("general.ValidationFail"));
                setIsSubmitting(false); // Re-enable the button
                return;
            }

        } catch (error) {
            // General error handling
            errorToast(error, t("general.Error"), t);
        } finally {
            // Ensure the button is re-enabled regardless of outcome
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {(!passportRegion || !roomQuantity) ?
                <div className='info-container'>
                    <h3>{t("accomodation.title")}</h3>
                    <p>
                        {
                            languageSelected === "en"
                                ? courseData.course_ename
                                : courseData.course_cname
                        }
                    </p>
                    {isLoading ?
                        <>
                            <AiOutlineLoading className='spin' />
                            <p>{t("accomodation.Loading1")}</p>
                        </>
                        :
                        <>
                            <p style={{ "color": "#ff0000" }}><Trans key="accommodation" html="br:<br/>">
                                <br />{t("accomodation.Ppfirst")}
                            </Trans></p>
                            <div className='action-buttons'>
                                <Link to={`${base_url}/submit-passport`}>
                                    <Button
                                        buttonStyle='btn--primary'
                                        buttonSize='btn--small'
                                    >{t("general.SubmitInfo")}</Button>
                                </Link>

                            </div>
                        </>
                    }
                </div>
                :
                <>
                    <div className='info-container'>
                        <h3>{t("accomodation.title")}</h3>
                        <p>
                            {
                                languageSelected === "en"
                                    ? courseData.course_ename
                                    : courseData.course_cname
                            }
                        </p>
                        <div className='action-buttons'>
                            <Button buttonStyle='btn--primary' buttonSize='btn--small' onClick={handleCancel} type="button">{t("general.BTNcancel")}</Button>
                        </div>
                    </div>
                    <div className='form-wrapper'>
                        <h4 className='d-flex mt-3 justify-content-left'>{t("general.Page")} {formSection} {t("general.OF3")}</h4>
                        <div className="progress mb-5">
                            <div className="progress-bar bg-success" role="progressbar" style={{ "width": formPercentage }} aria-valuenow="25" aria-valuemin="0" aria-valuemax="100"></div>
                        </div>
                        <form>
                            <div className='form-section-1' hidden={formSection !== 1}>
                                <p>

                                    ☘️
                                    <strong> {t("accomodation.title3")}</strong>
                                    {t("accomodation.title3_1")}
                                    <br />
                                    <strong>{t("accomodation.registerTimeLabel")}</strong>
                                    <br />
                                    {
                                        languageSelected === "en" ?
                                            <>
                                                {dayjs.utc(courseData.start_datetime).subtract(1, 'day').format("dddd, D MMMM YYYY, [3:00 PM - 10:00 PM]")}
                                                <br />
                                                {dayjs.utc(courseData.start_datetime).format("dddd, D MMMM YYYY, [7:30 AM - 9:30 AM]")}
                                            </>
                                            :
                                            <>
                                                {
                                                    dayjs.utc(courseData.start_datetime).subtract(1, 'day')
                                                        .locale("zh-cn")
                                                        .format("YYYY年M月D日 (dddd) [下午 3:00 PM - 晚上 10:00 PM]")
                                                }
                                                <br />
                                                {
                                                    dayjs.utc(courseData.start_datetime)
                                                        .locale("zh-cn")
                                                        .format("YYYY年M月D日 (dddd) [早上 7:30 AM - 早上 9:30 AM]")
                                                }
                                            </>

                                    }
                                    <br /><br />
                                    <strong>{t("accomodation.endTimeLabel")}</strong>
                                    <br />
                                    {
                                        languageSelected === "en" ?
                                            dayjs.utc(courseData.end_datetime).format("dddd, D MMMM YYYY, h:mm A")
                                            :
                                            dayjs.utc(courseData.end_datetime)
                                                .locale("zh-cn")
                                                .format("YYYY年M月D日 (dddd) A h:mm [PM]")
                                    }
                                </p>
                                <br />
                                <p>
                                    ☘️
                                    <strong> {t("accomodation.TitleFee")}</strong>
                                </p>
                                <p>
                                    <strong>{t("accomodation.TitleRoom")}</strong>
                                    <br />
                                    <Trans key="accommodation" html="br:<br/>">
                                        {
                                            t("accomodation.RoomOP", {
                                                single_room_price: kingRoom1PPrice,
                                                double_room_price: twinRoom2PPrice,
                                            })
                                        }
                                    </Trans>

                                </p>
                                <p>
                                    <strong>{t("accomodation.roomTypeTitle")}</strong>
                                    <br />
                                    <Trans key="accommodation" html="br:<br/>">
                                        {t("accomodation.roomTypeDescription")}
                                    </Trans>
                                </p>
                                <p><br /><strong>{t("accomodation.includeFeesTitle")}</strong>
                                    <Trans key="accommodation" html="br:<br/>">
                                        <br />
                                        {
                                            t(
                                                "accomodation.roomFees",
                                                {
                                                    start_date: languageSelected === "en" ?
                                                        dayjs.utc(courseData.start_datetime).format("D MMMM")
                                                        :
                                                        dayjs.utc(courseData.start_datetime)
                                                            .locale("zh-cn")
                                                            .format("M月D日"),

                                                    end_date: languageSelected === "en" ?
                                                        dayjs.utc(courseData.end_datetime).format("D MMMM")
                                                        :
                                                        dayjs.utc(courseData.end_datetime)
                                                            .locale("zh-cn")
                                                            .format("M月D日"),

                                                    date_range: languageSelected === "en"
                                                        ? `${dayjs.utc(courseData.start_datetime).add(1, "day").format("D")} - ${dayjs.utc(courseData.end_datetime).subtract(1, "day").format("D MMMM")}`
                                                        : `${dayjs.utc(courseData.start_datetime).add(1, "day").locale("zh-cn").format("M月D日")} - ${dayjs.utc(courseData.end_datetime).subtract(1, "day").locale("zh-cn").format("M月D日")}`
                                                }
                                            )
                                        }
                                    </Trans>
                                </p>
                                <p>
                                    <br />
                                    <strong>{t("accomodation.Sentence1")}</strong>
                                    <span style={{ "color": "#ff0000" }}>
                                        <strong> {t("accomodation.Sentence2")}</strong>
                                    </span>:
                                    <br />{t("accomodation.feeContent1")}
                                    <br />{t("accomodation.feeContent6")}
                                    <br /><br />{t("accomodation.feeContent6_1")}
                                    <br /><a href="https://www.988.com.my/topic/malaysia-news/251121-sustainable-charges/" target="_blank" rel="noopener noreferrer">https://www.988.com.my/topic/malaysia-news/251121-sustainable-charges/</a>
                                    <br /><a href="https://mediaselangor.com/en/2025/11/331650" target="_blank" rel="noopener noreferrer">https://mediaselangor.com/en/2025/11/331650</a>
                                    <br /><a href={`${base_url}/pdf_template/hotel_sustainability_fees_letter.pdf`} target="_blank" rel="noopener noreferrer">{t("accomodation.sustainabilityLetter")}</a>
                                </p>
                                <p>&nbsp;</p>
                                <p>
                                    ☘️<strong> {t("accomodation.BefnAft")}</strong>
                                </p>
                                <p>
                                    <strong>{t("accomodation.roomOP2")}</strong>
                                    <br /><Trans key="accomodation" html="br:<br/>">
                                        {
                                            t("accomodation.roomFee2", {
                                                extend_stay_price: extendStayPrice,
                                            })
                                        }
                                    </Trans>

                                </p>
                                <p><br />
                                    <strong>{t("accomodation.reservationSuggestion")}</strong>
                                    <br />
                                    {t(
                                        "accomodation.OP2content",
                                        {
                                            check_in: languageSelected === "en" ?
                                                dayjs.utc(courseData.start_datetime).format("h:mm A on D MMMM")
                                                :
                                                dayjs.utc(courseData.start_datetime).locale("zh-cn").format("M月D日 A h:mm [AM]"),

                                            prior_one_day: languageSelected === "en" ?
                                                dayjs.utc(courseData.start_datetime).subtract(1, "day").format("D MMMM")
                                                :
                                                dayjs.utc(courseData.start_datetime).subtract(1, "day").locale("zh-cn").format("M月D日"),
                                        }
                                    )}
                                </p>
                                <p>{t("accomodation.OP2content1")}&nbsp;
                                    <strong>
                                        {t("accomodation.OP2content2")}&nbsp;
                                        <span style={{ "color": "#ff0000" }}>
                                            {t("accomodation.OP2content3")}
                                        </span>
                                    </strong>
                                </p>
                                <p>&nbsp;</p>
                                <p><strong>{t("accomodation.reminder")}</strong>
                                    <br />📌 {t("accomodation.policy")}
                                    <span style={{ "color": "#ff0000" }}><strong> {t("accomodation.norefund")}</strong></span>{t("accomodation.thank")}</p>
                                <p>&nbsp;</p>
                            </div>
                            <div className='form-section-2' hidden={formSection !== 2}>
                                <p>
                                    {t("accomodation.Warn")}
                                </p>
                                {<p>
                                    {t("accomodation.Warn1")}
                                    <strong> {t("accomodation.Warn1_2")}</strong>
                                </p>}
                                <p>
                                    {t("accomodation.Warn2")}
                                    <strong> {t("accomodation.Warn2_1")} </strong>
                                    {t("accomodation.Warn2_2")}
                                </p>
                                <Trans key="accomodation" html="br:<br/>">
                                    {t("accomodation.Warn3")}
                                </Trans>
                                <div className="form-item mt-5">
                                    <label>{t("accomodation.Pledge")}<span style={{ "color": "#ff0000" }}>*</span></label>
                                    <div className="d-flex gap-2">
                                        <input type="checkbox" className="form-check-input" id="is_compliance_health_measure" onChange={() => setIsPledgeChecked(!isPledgeChecked)} />
                                        <p className='m-0'>{t("accomodation.Pledge2")}</p>
                                    </div>
                                </div>
                            </div>
                            <div className='form-section-3' hidden={formSection !== 3}>
                                <div>
                                    <p><strong>{t("accomodation.reminder")}</strong>
                                        <br />📌 {t("accomodation.policy")}
                                        <span style={{ "color": "#ff0000" }}><strong> {t("accomodation.norefund")}</strong></span>{t("accomodation.thank")}</p>
                                    <AccommodationOption />
                                    <div className="form-item mt-5">
                                        <label>
                                            <span style={{ color: "#ff0000", fontSize: "20px" }}>{t("accomodation.RoomType")}*</span>
                                        </label>
                                        <div className="image-radio-container">
                                            <button
                                                type='button'
                                                disabled={isRoomDisabled('doubleroom')}
                                                className={`room-radio-option ${selectedRoom === 'twin_2p' ? 'selected' : ''} ${isRoomDisabled('doubleroom') ? 'disabled' : ''}`}
                                                onClick={() => handleRoomSelection('twin_2p')}
                                            >
                                                <span>
                                                    <img
                                                        src={`${base_url}/images/twinroom.JPG`}
                                                        alt={t("Constant.TwinRoom")}
                                                    />
                                                    {selectedRoom === 'twin_2p' && <div className="tick-icon">✔</div>}
                                                </span>
                                                <p>{t("Constant.TwinRoom")} (MYR {twinRoom2PPrice}) {isRoomDisabled('doubleroom') && <span className="error">{t("accomodation.fullyBooked")}</span>}</p>
                                            </button>
                                            <button
                                                type='button'
                                                disabled={isRoomDisabled('doubleroom')}
                                                className={`room-radio-option ${selectedRoom === 'king_2p' ? 'selected' : ''} ${isRoomDisabled('doubleroom') ? 'disabled' : ''}`}
                                                onClick={() => handleRoomSelection('king_2p')}
                                            >
                                                <span>
                                                    <img
                                                        src={`${base_url}/images/kingroom.JPG`}
                                                        alt={t("Constant.KingRoomDoubleOccupancy")}
                                                    />
                                                    {selectedRoom === 'king_2p' && <div className="tick-icon">✔</div>}
                                                </span>
                                                <p>{t("Constant.KingRoomDoubleOccupancy")} (MYR {kingRoom2PPrice}) {isRoomDisabled('doubleroom') && <span className="error">{t("accomodation.fullyBooked")}</span>}</p>
                                            </button>
                                            <button
                                                type='button'
                                                disabled={isRoomDisabled('singleroom')}
                                                className={`room-radio-option ${selectedRoom === 'king_1p' ? 'selected' : ''} ${isRoomDisabled('singleroom') ? 'disabled' : ''}`}
                                                onClick={() => handleRoomSelection('king_1p')}
                                            >
                                                <span>
                                                    <img
                                                        src={`${base_url}/images/kingroom.JPG`}
                                                        alt={t("Constant.KingRoom")}
                                                    />
                                                    {selectedRoom === 'king_1p' && <div className="tick-icon">✔</div>}
                                                </span>
                                                <p>{t("Constant.KingRoom")} (MYR {kingRoom1PPrice}) {isRoomDisabled('singleroom') && <span className="error">{t("accomodation.fullyBooked")}</span>}</p>
                                            </button>
                                        </div>
                                    </div>
                                    {errors.room_type && <span className="error">{errors.room_type}</span>}
                                    {
                                        selectedRoom &&
                                        <>
                                            {/* <div className="form-item">
                                                {selectedRoom === "twinroom" ?
                                                    <label>{t("accomodation.IFtwin")}
                                                        <span style={{ "color": "#ff0000" }}>*</span>
                                                    </label>
                                                    :
                                                    <label>{t("accomodation.IFking")}
                                                        <span style={{ "color": "#ff0000" }}>*</span>
                                                    </label>
                                                }
                                                <div className='radio'>
                                                    <input className="form-check-input p-2" type="radio" name="is_addon_bed" id="1" onChange={handleInputChange} required />
                                                    <p className='radioLabel mb-0'>{t("general.yes")} {t("accomodation.notCOMFORTABLE")}</p>
                                                </div>
                                                <div className='radio'>
                                                    <input className="form-check-input p-2" type="radio" name="is_addon_bed" id="0" onChange={handleInputChange} required />
                                                    <p className='radioLabel mb-0'>{t("general.no")}</p>
                                                </div>
                                            </div>
                                            {errors.is_addon_bed && <span className="error">{errors.is_addon_bed}</span>} */}
                                            {
                                                selectedRoom !== "king_1p" &&
                                                <>
                                                    <div className="form-item">
                                                        <label>{t("accomodation.Roommate_Question")}
                                                            <span style={{ "color": "#ff0000" }}>*</span></label>
                                                        <div className='radio'>
                                                            <input className="form-check-input" type="radio" name="is_roommate" id="1" onChange={handleInputChange} checked={formData?.is_roommate === "1"} />
                                                            <p className='radioLabel mb-0'>{t("general.yes")}</p>
                                                        </div>
                                                        <div className='radio'>
                                                            <input className="form-check-input p-2" type="radio" name="is_roommate" id="0" onChange={handleInputChange} checked={formData?.is_roommate === "0"} />
                                                            <p className='radioLabel mb-0'>{t("accomodation.roomArrange")}</p>
                                                        </div>
                                                    </div>
                                                    {errors.is_roommate && <span className="error">{errors.is_roommate}</span>}
                                                </>
                                            }

                                            {formData?.is_roommate && formData?.is_roommate === "1" &&
                                                <>
                                                    <div className="form-item">
                                                        <label for="roommate_id" className="form-label">{t("accomodation.Roommate_ID")}<span style={{ "color": "#ff0000" }}>*</span></label>
                                                        <input type="text" className="form-control" id="roommate_id" placeholder="" onChange={handleInputChange} required />
                                                    </div>
                                                    {errors.roommate_id && <span className="error">{errors.roommate_id}</span>}
                                                    <div className="form-item">
                                                        <label for="roommate_name" className="form-label">{t("accomodation.Roommate_Name")}<span style={{ "color": "#ff0000" }}>*</span></label>
                                                        <input type="text" className="form-control" id="roommate_name" placeholder="" onChange={handleInputChange} required />
                                                    </div>
                                                    {errors.roommate_name && <span className="error">{errors.roommate_name}</span>}
                                                    <div className="form-item">
                                                        <label for="roommate_relationship" className="form-label">{t("accomodation.Roommate_Relationship")}<span style={{ "color": "#ff0000" }}>*</span></label>
                                                        <input type="text" className="form-control" id="roommate_relationship" placeholder="" onChange={handleInputChange} required />
                                                    </div>
                                                    {errors.roommate_relationship && <span className="error">{errors.roommate_relationship}</span>}
                                                </>
                                            }
                                            <div className='form-item'>
                                                <label for="check_in_date" className="form-label">{t("accomodation.checkInDate")}<span style={{ "color": "#ff0000" }}>*</span></label>
                                                <input
                                                    type='date'
                                                    id="check_in_date"
                                                    onChange={handleInputChange}
                                                    value={formData.check_in_date || ""}
                                                    min={
                                                        dayjs(courseData.start_datetime)
                                                            .utc()
                                                            .subtract(5, "day")
                                                            .format("YYYY-MM-DD")
                                                    }
                                                    max={
                                                        dayjs(courseData.start_datetime)
                                                            .utc()
                                                            .format("YYYY-MM-DD")
                                                    }
                                                />
                                            </div>
                                            {errors.check_in_date && <p className='m-0'><span className="error">{errors.check_in_date}</span></p>}
                                            <div className='form-item'>
                                                <label for="check_out_date" className="form-label">{t("accomodation.checkOutDate")}<span style={{ "color": "#ff0000" }}>*</span></label>
                                                <input
                                                    type='date'
                                                    id="check_out_date"
                                                    onChange={handleInputChange}
                                                    value={formData.check_out_date || ""}
                                                    min={
                                                        dayjs(courseData.end_datetime)
                                                            .utc()
                                                            .format("YYYY-MM-DD")
                                                    }
                                                    max={
                                                        dayjs(courseData.end_datetime)
                                                            .utc()
                                                            .add(1, "day")
                                                            .format("YYYY-MM-DD")
                                                    }
                                                />
                                            </div>
                                            {errors.check_out_date && <p className='m-0'><span className="error">{errors.check_out_date}</span></p>}
                                            <div className="form-item">
                                                <label>{t("accomodation.PaymentMethod")}
                                                    <span style={{ "color": "#ff0000" }}>*</span></label>
                                                <div className='radio'>
                                                    <input className="form-check-input p-2" type="radio" name="payment_mode" id="stripe" onChange={handleInputChange} />
                                                    <p className='radioLabel mb-0'>{t("accomodation.Method1")}</p>
                                                </div>
                                                <div className='radio'>
                                                    <input className="form-check-input p-2" type="radio" name="payment_mode" id="offline" onChange={handleInputChange} />
                                                    <p className='radioLabel mb-0'>{t("accomodation.Method2")}<span style={{ color: "#ff0000" }}>&nbsp;{t("accomodation.Method2Remind")}</span></p>
                                                </div>
                                            </div>
                                            {errors.payment_mode && <span className="error">{errors.payment_mode}</span>}

                                            <h4 className='mt-4'>{t("accomodation.CheckoutAmount")}</h4>
                                            <table className="table table-light table-striped">
                                                <thead>
                                                    <tr>
                                                        <th scope="col">{t("accomodation.AmtTitle")}</th>
                                                        <th scope="col">{t("accomodation.AmtItem")}</th>
                                                        <th scope="col">{t("accomodation.AMT")}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <th className='th-row' scope="row">{t("accomodation.Roomselect")}</th>
                                                        <td>{invoiceItemCopy.filter((item) => item.key === formData.room_type).map((item) => item.title)}</td>
                                                        <td>MYR {checkoutItem.selectedRoomAmount}</td>
                                                    </tr>
                                                    <tr>
                                                        <th className='th-row' scope="row">{t("accomodation.Early_C_I")}</th>
                                                        <td>{earlyStayDay} {t("accomodation.nights")}</td>
                                                        <td>MYR {checkoutItem.earlyStayAmount}</td>
                                                    </tr>
                                                    <tr>
                                                        <th className='th-row' scope="row">{t("accomodation.Extend_C_O")}</th>
                                                        <td>{extendStayDay} {t("accomodation.nights")}</td>
                                                        <td>MYR {checkoutItem.extendStayAmount}</td>
                                                    </tr>
                                                    <tr>
                                                        <th className='th-row' scope="row">{t("accomodation.TourismTax")}</th>
                                                        <td>{passportRegion === "Malaysia" ? t("accomodation.msia") : `${7 + earlyStayDay + extendStayDay} ${t("accomodation.days")} ${extendStayDay + earlyStayDay + 6} ${t("accomodation.nights")}`}</td>
                                                        <td>MYR {checkoutItem.tourismTax}</td>
                                                    </tr>
                                                    {/* <tr>
                                                        <th className='th-row' scope="row">{t("accomodation.TransactionFees")}</th>
                                                        <td>{formData.payment_mode === "creditcard" ? t("accomodation.CreditCard") : t("accomodation.OfflineTransfer")}</td>
                                                        <td>MYR {transactionFee}</td>
                                                    </tr> */}
                                                    <tr>
                                                        <th className='th-row' scope="row">{t("accomodation.sustainabilityFee")}</th>
                                                        <td>{extendStayDay + earlyStayDay + 7} {t("accomodation.days")} {extendStayDay + earlyStayDay + 6} {t("accomodation.nights")}</td>
                                                        <td>MYR {checkoutItem.sustainabilityFee}</td>
                                                    </tr>
                                                    <tr>
                                                        <td></td>
                                                        <td className='th-row'><strong>{t("accomodation.TotalRM")} ({t("accomodation.myr")})</strong></td>
                                                        <td>
                                                            {
                                                                formData.room_type ?
                                                                    formData.check_in_date && formData.check_out_date ?
                                                                        <strong>{totalAmount.totalAmount}</strong> : <span style={{ color: "#ff0000" }}>{t("accomodation.chooseStayDate")}</span> :
                                                                    <span style={{ color: "#ff0000" }}>{t("accomodation.tickRoomType")}</span>
                                                            }
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td></td>
                                                        <td className='th-row'><strong>{t("accomodation.TotalCurrency")} ({currencySelected.key || "MYR"})</strong></td>
                                                        <td>
                                                            {
                                                                formData.room_type ?
                                                                    formData.check_in_date && formData.check_out_date ?
                                                                        <strong>{totalAmount.totalAmountInCurrencySelected}</strong> : <span style={{ color: "#ff0000" }}>{t("accomodation.chooseStayDate")}</span> :
                                                                    <span style={{ color: "#ff0000" }}>{t("accomodation.tickRoomType")}</span>
                                                            }
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                            {paymentMethod === "offline" &&
                                                <>
                                                    <div className="form-item">
                                                        <label>{t("accomodation.cURRENCY")}
                                                            <span style={{ "color": "#ff0000" }}>*</span></label>
                                                        <select className="form-select" onChange={handleCurrencyChange} id="offline_payment_currency" required>
                                                            <option value="">{t("accomodation.ChooseCurrency")}</option>
                                                            {currencyCopy.map((item, index) => (
                                                                <option key={index} value={item.key}>{item.currency}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                    {errors.offline_payment_currency && <span className="error">{errors.offline_payment_currency}</span>}
                                                    {
                                                        currencySelected !== "" &&
                                                        bankInfoCopy
                                                            .filter((i) => i.key === currencySelected.key)
                                                            .map((i, index) => (
                                                                <div className='section-title mb-2 mt-4' key={index}>
                                                                    <p>
                                                                        <strong>{i.title}</strong>
                                                                        <br />
                                                                        {i.subtitle}
                                                                        <br />
                                                                        {i.bank && <>{t("accomodation.bank")} {i.bank}<br /></>}
                                                                        {i.image && <><img src={i.image} style={{ "width": "250px" }} alt="" /><br /></>}
                                                                        {i.account && <>{t("accomodation.acc_Num")} {i.account} <br /></>}
                                                                        {i.name && <>{t("accomodation.acc_Name")} {i.name}<br /></>}
                                                                        {i.shiftcode && `SWIFT Code: ${i.shiftcode}`}
                                                                    </p>
                                                                </div>
                                                            ))
                                                    }
                                                    <div className="form-item">
                                                        <label for="transaction-prove" className="form-label">
                                                            {t("accomodation.Upload_Res")}
                                                            <span style={{ "color": "#ff0000" }}>*</span></label>
                                                        {
                                                            previews.offline_payment_prove &&
                                                            <img src={previews.offline_payment_prove} className="img-thumbnail mt-3" style={{ maxWidth: "200px" }} />
                                                        }
                                                        <input
                                                            type="file"
                                                            className="form-control"
                                                            onChange={handleFileChange}
                                                            id="offline_payment_prove"
                                                        />
                                                    </div>
                                                    <div className="form-info mt-2">
                                                        <p className='mb-0'>{t("general.smallerFile10MB")}</p>
                                                    </div>
                                                    {fileError?.offline_payment_prove && <div className="error mt-2">{fileError?.offline_payment_prove}</div>}
                                                    {errors.offline_payment_prove && <span className="error">{errors.offline_payment_prove}</span>}
                                                </>
                                            }
                                            {paymentMethod === "stripe" &&
                                                <Elements
                                                    stripe={stripePromise}
                                                    options={{
                                                        mode: "payment",
                                                        // amount: convertToSubcurrency(((Number(netTotalAmount) + Number(checkoutItem.tourismTax) + 1) / 0.96).toFixed(2)),
                                                        amount: convertToSubcurrency(Object.values(checkoutItem).reduce((sum, val) => sum + Number(val || 0), 0).toFixed(2)),
                                                        currency: "myr",
                                                    }}
                                                >
                                                    <Checkout
                                                        amount={(Object.values(checkoutItem).reduce((sum, val) => sum + Number(val || 0), 0)).toFixed(2)}
                                                        formData={setNullFormToEmpty(formData)}
                                                        validateAllFields={validateAllFields}
                                                        checkoutItem={checkoutItem}
                                                        // uncommented for transaction fee removal
                                                        // transactionFee={Number(((0.04 * (Number(netTotalAmount) + Number(checkoutItem.tourismTax)) + 1) / 0.96))}
                                                        netTotalAmount={Number(netTotalAmount)}
                                                        generateValidationFields={generateValidationFields}
                                                        isFull={isFull}
                                                        topUp={false}
                                                    />
                                                </Elements>
                                            }
                                        </>
                                    }
                                </div>
                            </div>
                            <div className='button-container mt-3'>
                                <Button buttonStyle="btn--primary" hidden={formSection === 1} type="button" onClick={() => navigateFormSection(false)}>{t("general.BTNprevious")}</Button>
                                <Button buttonStyle="btn--primary" hidden={formSection === 3} type="button" onClick={() => navigateFormSection(true)}>{t("general.BTNnext")}</Button>
                                {paymentMethod === "offline" &&
                                    <Button buttonStyle="btn--primary" hidden={formSection !== 3} onClick={handleSubmit} type="button">
                                        {isSubmitting ? <AiOutlineLoading className="spin" /> : t("general.BTNsubmit")}
                                    </Button>
                                }
                            </div>

                        </form>
                    </div>
                </>
            }
        </>
    )
}

export default CreateAccomodation